import React, { useEffect, useState } from "react";
import { CheckCircle } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import logger from "@/utils/logger";

const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [remainingSeconds, setRemainingSeconds] = useState(5); // Set to 5 seconds as requested
  const [sessionRefreshed, setSessionRefreshed] = useState(false);
  // Track if user has manually navigated
  const [hasManuallyNavigated, setHasManuallyNavigated] = useState(false);
  
  // Handle manual navigation to dashboard
  const handleManualNavigation = () => {
    setHasManuallyNavigated(true);
    // Log the manual navigation
    logger.info("User manually navigated to dashboard");
    window.location.href = "/dashboard";
  };

  // Split the effects to avoid potential issues with state updates
  // First effect: Handle session refresh
  useEffect(() => {
    const refreshSession = async () => {
      try {
        // Get current session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          logger.error("No session found, attempting to refresh auth");
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          
          if (refreshError || !refreshData.session) {
            logger.error("Failed to refresh auth session:", refreshError);
            toast.error("Your session has expired. Please log in again.");
            setTimeout(() => {
              window.location.replace("/login?return_to=dashboard&payment_completed=true");
            }, 2000);
            return;
          }
        }
        
        // Refresh user data
        await refreshUser();
        logger.info("User session refreshed after subscription success");
        
        // Mark session as refreshed
        setSessionRefreshed(true);
        
        // Show success toast
        toast.success(
          "Your subscription was completed successfully! Welcome to premium!",
          { duration: 5000, id: "subscription-success" }
        );
        
        // Clean up URL parameters
        const url = new URL(window.location.href);
        url.searchParams.delete("subscription");
        url.searchParams.delete("session_refresh");
        window.history.replaceState({}, "", url.toString());
      } catch (error) {
        logger.error("Error refreshing session:", error);
        toast.error("Error refreshing session. Please log in again.");
      }
    };
    
    refreshSession();
  }, [refreshUser]);
  
  // Second effect: Handle countdown and redirect
  // This is separate to ensure it runs independently of session refresh
  useEffect(() => {
    logger.info("Initializing countdown timer with 5 seconds");
    
    // Create an interval that runs every second
    const countdownInterval = setInterval(() => {
      setRemainingSeconds(seconds => {
        // Log the countdown
        const newValue = seconds - 1;
        logger.info(`Countdown: ${seconds} → ${newValue}`);
        
        // Return the new value
        return newValue;
      });
    }, 1000);
    
    // Create a timeout for the redirect that will happen after 5 seconds
    const redirectTimeout = setTimeout(() => {
      logger.info("Countdown complete, redirecting to dashboard");
      
      if (!hasManuallyNavigated) {
        // Use the most reliable redirection method
        window.location.href = "/dashboard";
      }
    }, 5500); // Slightly longer than 5 seconds to account for processing time
    
    // Safety net in case the main redirect fails
    const safetyTimeout = setTimeout(() => {
      logger.info("Safety redirect check");
      if (!hasManuallyNavigated) {
        logger.info("Safety redirect triggered");
        window.location.replace("/dashboard");
      }
    }, 7000);
    
    // Cleanup all timers when component unmounts
    return () => {
      clearInterval(countdownInterval);
      clearTimeout(redirectTimeout);
      clearTimeout(safetyTimeout);
    };
  }, [hasManuallyNavigated]); // Only re-run if hasManuallyNavigated changes

  return (
    <div className="max-w-md mx-auto text-center py-16 px-4">
      <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
      <h1 className="text-2xl font-bold mb-4">Subscription Successful!</h1>
      <p className="text-gray-600 mb-6">
        Thank you for subscribing to PetDocument. Your account has been upgraded
        and you now have access to all premium features.
      </p>
      <div className="bg-blue-50 p-4 rounded-md mb-6">
        <p className="text-gray-700 mb-2">
          You will be automatically redirected to your dashboard in{" "}
          <span className="inline-block min-w-[1.5rem] font-bold text-lg text-blue-600 bg-blue-100 px-2 py-1 rounded-md animate-pulse">
            {remainingSeconds}
          </span>{" "}
          seconds...
        </p>
        <p className="text-sm text-blue-700">
          If you are not redirected automatically, please click the button below.
        </p>
      </div>
      <Button
        onClick={handleManualNavigation}
        className="w-full bg-primary hover:bg-primary/90"
      >
        Go to Dashboard Now
      </Button>
    </div>
  );
};

export default SubscriptionSuccess;
